import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, AED_short } from '../../lib/api';
import MobileShell from '../../components/MobileShell';
import { Search, MapPin, ChevronRight } from 'lucide-react';

const FILTERS = [
  { key: 'all',         label: 'All',         match: () => true },
  { key: 'residential', label: 'Residential', match: (p) => ['apartment','penthouse','townhouse','villa'].includes(p.property_type) },
  { key: 'commercial',  label: 'Commercial',  match: (p) => ['commercial_office','retail'].includes(p.property_type) },
  { key: 'land',        label: 'Land',        match: (p) => p.property_type === 'plot' },
];

const STAGE_TINT = {
  sourcing:    'bg-slate-100 text-slate-700',
  under_offer: 'bg-amber-100 text-amber-800',
  owned:       'bg-blue-100 text-blue-800',
  refurb:      'bg-[#EAE3D9] text-amber-900',
  listed:      'bg-purple-100 text-purple-800',
  sold:        'bg-emerald-100 text-emerald-800',
};

export default function MobileProperties() {
  const [props, setProps] = useState([]);
  const [f, setF] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/properties').then(r => { setProps(r.data); setLoading(false); });
  }, []);

  const filtered = useMemo(() => {
    const flt = FILTERS.find(x => x.key === f);
    const s = q.trim().toLowerCase();
    return props.filter(flt.match).filter(p => {
      if (!s) return true;
      return (p.title + ' ' + p.community + ' ' + (p.building || '')).toLowerCase().includes(s);
    });
  }, [props, f, q]);

  return (
    <MobileShell title="Properties" noHeader>
      <div className="pt-3 pb-4">
        <h1 className="font-serif text-3xl mb-1">Properties</h1>
        <p className="text-xs text-slate-500">{filtered.length} of {props.length}</p>
      </div>

      {/* Search bar */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, community, building…"
          data-testid="m-props-search"
          className="w-full pl-10 pr-3 py-3 rounded-2xl bg-white/85 border border-white/70 text-sm outline-none focus:ring-2 focus:ring-[#679286]/30"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-3 scrollbar-none" data-testid="m-props-filters">
        {FILTERS.map(x => (
          <button
            key={x.key}
            onClick={() => setF(x.key)}
            data-testid={`m-filter-${x.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              f === x.key ? 'bg-[#1A2530] text-white shadow-md' : 'bg-white/70 text-slate-700 border border-white/60'
            }`}
          >
            {x.label} <span className={f === x.key ? 'opacity-80' : 'opacity-50'}>· {props.filter(x.match).length}</span>
          </button>
        ))}
      </div>

      {loading && <SkeletonList/>}

      {!loading && (
        <div className="space-y-3" data-testid="m-props-list">
          {filtered.map(p => <PropertyCard key={p.id} p={p}/>)}
          {filtered.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-400">No matches. Try clearing filters.</div>
          )}
        </div>
      )}
    </MobileShell>
  );
}

function PropertyCard({ p }) {
  const purchase = p.purchase_price_aed || p.asking_price_aed;
  const roi = p.arv_aed && purchase
    ? (((p.arv_aed - (purchase + (p.refurb_budget_aed || 0))) / (purchase + (p.refurb_budget_aed || 0))) * 100).toFixed(0)
    : null;
  return (
    <Link
      to={`/m/properties/${p.id}`}
      data-testid={`m-property-${p.id}`}
      className="block rounded-2xl overflow-hidden bg-white/85 border border-white/70 shadow-sm active:scale-[0.98] transition-transform"
    >
      {p.thumbnail_url && (
        <div className="h-40 bg-slate-200 relative">
          <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover"/>
          <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest ${STAGE_TINT[p.stage] || 'bg-slate-100 text-slate-700'}`}>
            {p.stage?.replace('_',' ')}
          </span>
          {p.deal_score != null && (
            <div className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white/95 grid place-items-center shadow-md">
              <div className="text-center leading-none">
                <div className="text-[8px] uppercase tracking-widest text-slate-500">Score</div>
                <div className="font-serif text-lg text-[#679286]">{p.deal_score}</div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1"><MapPin size={11}/> {p.community}{p.building ? ` · ${p.building}` : ''}</div>
        <div className="font-medium text-slate-900 truncate">{p.title}</div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div><div className="text-slate-500">Purchase</div><div className="font-medium truncate">{AED_short(purchase)}</div></div>
          <div><div className="text-slate-500">ARV</div><div className="font-medium truncate">{AED_short(p.arv_aed)}</div></div>
          {roi && <div><div className="text-slate-500">ROI</div><div className={`font-semibold ${roi >= 15 ? 'text-emerald-600' : 'text-amber-600'}`}>{roi}%</div></div>}
        </div>
      </div>
      <div className="px-4 py-2 border-t border-slate-100/70 flex items-center justify-between text-xs text-[#679286] font-medium">
        <span>Open details</span>
        <ChevronRight size={14}/>
      </div>
    </Link>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {[1,2,3].map(i => (
        <div key={i} className="rounded-2xl bg-white/60 border border-white/70 overflow-hidden animate-pulse">
          <div className="h-40 bg-slate-200/70"/>
          <div className="p-4 space-y-2">
            <div className="h-3 w-24 bg-slate-200 rounded"/>
            <div className="h-4 w-3/4 bg-slate-200 rounded"/>
            <div className="h-3 w-1/2 bg-slate-200 rounded"/>
          </div>
        </div>
      ))}
    </div>
  );
}
